import { useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useMatchDetail, type SwapSticker } from './useMatches';
import { Check } from '../theme/icons';
import { teamThemeOf } from '../theme/teamPalette';

export default function SwapDetailPage() {
  const { slug, friendId } = useParams<{ slug: string; friendId: string }>();
  const navigate = useNavigate();
  if (!slug || !friendId) return <Navigate to="/albums" replace />;

  const { data, isLoading, error } = useMatchDetail(slug, friendId);
  const [checked, setChecked] = useState<Set<string>>(new Set());

  function toggle(key: string) {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  if (isLoading) return <p className="p-6 text-muted">Lade…</p>;
  if (error || !data) return <p className="p-6 text-status-double">Konnte Detail nicht laden</p>;

  const palette = teamThemeOf(data.friend.favoriteTeam);

  return (
    <div className="min-h-full safe-top safe-bottom flex flex-col">
      <header className="sticky top-0 z-10 bg-bg border-b border-border/60 px-3 py-3 flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          aria-label="Zurück"
          className="w-[34px] h-[34px] rounded-[11px] bg-surface2 flex items-center justify-center text-text"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M15 5l-7 7 7 7" />
          </svg>
        </button>
        <div className="flex-1 min-w-0">
          <p className="font-sans text-[18px] font-extrabold text-text truncate">
            Tausch mit {data.friend.displayName}
          </p>
          <p className="font-mono text-[10px] text-muted mt-0.5">
            fairer Tausch · {data.iGive.length} ↔ {data.iGet.length}
          </p>
        </div>
        <span
          className="w-[42px] h-[42px] rounded-[13px] flex items-center justify-center font-mono text-[17px] font-bold text-white flex-none"
          style={{ background: palette.primary }}
        >
          {data.score}
        </span>
      </header>

      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        <Section
          title="ICH GEBE"
          hint="→ deine Doppelten"
          stickers={data.iGive}
          checked={checked}
          onToggle={toggle}
          arrow="right"
          arrowColor="var(--status-have)"
          keyPrefix="give"
        />
        <Section
          title="ICH KRIEGE"
          hint="→ ihre Doppelten"
          stickers={data.iGet}
          checked={checked}
          onToggle={toggle}
          arrow="left"
          arrowColor="var(--status-double)"
          keyPrefix="get"
        />
      </div>

      <div className="flex-none px-4 pb-4 pt-2 bg-bg">
        <p className="text-center font-sans text-[12px] text-muted mb-2">
          Abhaken = lokale Markierung. Bestand auf der Sticker-Seite aktualisieren, wenn du echt getauscht hast.
        </p>
      </div>
    </div>
  );
}

function Section({
  title,
  hint,
  stickers,
  checked,
  onToggle,
  arrow,
  arrowColor,
  keyPrefix,
}: {
  title: string;
  hint: string;
  stickers: SwapSticker[];
  checked: Set<string>;
  onToggle: (key: string) => void;
  arrow: 'left' | 'right';
  arrowColor: string;
  keyPrefix: string;
}) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <ArrowIcon dir={arrow} color={arrowColor} />
        <span className="font-mono text-[12px] font-bold tracking-[.1em] text-text">{title}</span>
        <span className="font-mono text-[11px] text-muted">{hint}</span>
      </div>
      {stickers.length === 0 ? (
        <p className="font-sans text-sm text-muted px-1">nichts</p>
      ) : (
        <ul className="space-y-2">
          {stickers.map((s) => {
            const k = `${keyPrefix}:${s.code}`;
            const isChecked = checked.has(k);
            return (
              <li key={k}>
                <button
                  type="button"
                  onClick={() => onToggle(k)}
                  className="w-full flex items-center gap-3 px-3.5 py-3 bg-surface border border-border rounded-[12px] text-left active:opacity-80"
                >
                  <span
                    className="w-[22px] h-[22px] rounded-[7px] flex items-center justify-center flex-none"
                    style={{
                      background: isChecked ? 'var(--status-have)' : 'transparent',
                      border: `1.5px solid ${isChecked ? 'var(--status-have)' : 'var(--cell-missing-border)'}`,
                    }}
                    aria-hidden="true"
                  >
                    {isChecked && <Check size={13} width={4} />}
                  </span>
                  <span
                    className="font-mono text-[14px] font-semibold tracking-[.02em] flex-1"
                    style={{
                      color: isChecked ? 'var(--color-muted)' : 'var(--color-text)',
                      textDecoration: isChecked ? 'line-through' : 'none',
                    }}
                  >
                    {s.code}
                  </span>
                  {s.team && (
                    <span className="font-mono text-[11px] text-muted">{s.team}</span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function ArrowIcon({ dir, color }: { dir: 'left' | 'right'; color: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {dir === 'right'
        ? <path d="M5 12h14M13 6l6 6-6 6" />
        : <path d="M19 12H5M11 6l-6 6 6 6" />}
    </svg>
  );
}
